import { forwardRef } from 'react';
import { Group, Button, Select, Text } from '@/components/ui';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import type { CCMSPaginationProps } from '../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

export const CCMSPagination = forwardRef<HTMLDivElement, CCMSPaginationProps>(
  (props, ref) => {
    const {
      page,
      pageSize,
      totalItems,
      onPageChange,
      onPageSizeChange,
      pageSizeOptions = [10, 20, 50, 100],
      showPageSize = true,
      gradientVariant = 'primary',
      className,
    } = props;

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const totalPages = Math.ceil(totalItems / pageSize);
    const startItem = (page - 1) * pageSize + 1;
    const endItem = Math.min(page * pageSize, totalItems);

    return (
      <Group justify="space-between" ref={ref} className={className}>
        <Group gap="sm">
          {showPageSize && onPageSizeChange && (
            <Group gap="xs">
              <Text size="sm" style={accessibilityStyles}>Show</Text>
              <Select
                data={pageSizeOptions.map(size => ({ value: String(size), label: String(size) }))}
                value={String(pageSize)}
                onChange={(value) => value && onPageSizeChange(Number(value))}
                size="xs"
                style={{ width: 80 }}
              />
              <Text size="sm" style={accessibilityStyles}>per page</Text>
            </Group>
          )}
          <Text size="sm" c="dimmed" style={accessibilityStyles}>
            {startItem}-{endItem} of {totalItems}
          </Text>
        </Group>

        <Group gap="xs">
          <Button
            variant="light"
            gradientVariant={gradientVariant}
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
          >
            <IconChevronLeft size={16} />
          </Button>
          <Text size="sm" fw={500} style={accessibilityStyles}>
            Page {page} of {totalPages}
          </Text>
          <Button
            variant="light"
            gradientVariant={gradientVariant}
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page === totalPages}
          >
            <IconChevronRight size={16} />
          </Button>
        </Group>
      </Group>
    );
  }
);

CCMSPagination.displayName = 'CCMSPagination';
export default CCMSPagination;
